import Phaser from 'phaser';
import { GameScene } from '../GameScene';
import { BOSS_COLOR, GAME_FONT_FAMILY } from '@shared/constants';
import type {
  NpcDialogueMessage,
  NpcRespawnedMessage,
  AreaEnteredMessage,
} from '@shared/types/messages';

/**
 * Handlers de mensajes del mundo enviados por el server: diálogo de NPC a la consola
 * (handleNpcDialogueMessage), toast de respawn de boss (handleNpcRespawned) y entrada a un área con
 * su cartel/banner (handleAreaEntered/showAreaBanner). Extraído de GameScene (god-object). El estado
 * (systemConsole, cámara…) vive en GameScene y se accede vía `this.scene.*`. Ver docs/AREAS.md.
 */
export class WorldMessageHandlers {
  constructor(private scene: GameScene) {}

  public handleNpcDialogueMessage(message: NpcDialogueMessage): void {
    if (message.message && message.message.trim()) {
      this.scene.chat.addChatConsoleMessage(message.npcName, message.message);
      this.scene.overheadText.showNpcChatBubble(message.npcId, message.message, message.color ?? '#ffffff');
    }
  }


  /**
   * Respawn de un NPC con notifyOnRespawn=true (típicamente boss). Toast a
   * toda la sala via SystemConsole; color dorado si isBoss, normal en otro
   * caso.
   */
  public handleNpcRespawned(message: NpcRespawnedMessage): void {
    const color = message.isBoss ? BOSS_COLOR : undefined;
    this.scene.chat.addSystemMessage(`¡Ha aparecido ${message.name}!`, color);
  }

  /**
   * El "área-banner" del player cambió. Si trae nombre, muestra un banner
   * centrado arriba (fade-in → hold → fade-out). Si viene vacío, lo limpia.
   * `musicId` se transporta para el futuro sistema de música (hoy no-op).
   */
  public handleAreaEntered(message: AreaEnteredMessage): void {
    const newId = message.areaId ?? null;
    if (newId === this.scene.currentAreaBannerId) return;
    this.scene.currentAreaBannerId = newId;

    if (message.musicId) {
      // TODO: arrancar música del área cuando exista el sistema de música.
      console.log(`[GameScene] (música futura) área "${message.name}" musicId=${message.musicId}`);
    }

    if (!message.name) {
      // Salió de toda área con nombre → limpiar banner.
      if (this.scene.areaBannerTween) { this.scene.areaBannerTween.stop(); this.scene.areaBannerTween = null; }
      if (this.scene.areaBannerText) { this.scene.areaBannerText.destroy(); this.scene.areaBannerText = null; }
      return;
    }

    this.showAreaBanner(message.name);
  }

  /** Banner centrado en la parte superior de la pantalla con el nombre del área. */
  public showAreaBanner(name: string): void {
    if (this.scene.areaBannerTween) { this.scene.areaBannerTween.stop(); this.scene.areaBannerTween = null; }
    if (this.scene.areaBannerText) { this.scene.areaBannerText.destroy(); this.scene.areaBannerText = null; }

    const { width } = this.scene.scale;
    const text = this.scene.add
      .text(width / 2, 150, name, {
        fontSize: '28px',
        fontFamily: GAME_FONT_FAMILY,
        color: '#ffe9a8',
        align: 'center',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5, 0.5)
      .setScrollFactor(0)
      .setDepth(5000)
      .setAlpha(0);
    this.scene.areaBannerText = text;

    // Fade-in → hold ~2.5s → fade-out.
    this.scene.areaBannerTween = this.scene.tweens.add({
      targets: text,
      alpha: 1,
      duration: 350,
      ease: 'Sine.easeOut',
      onComplete: () => {
        this.scene.time.delayedCall(2500, () => {
          if (!text.active) return;
          this.scene.areaBannerTween = this.scene.tweens.add({
            targets: text,
            alpha: 0,
            duration: 500,
            ease: 'Sine.easeIn',
            onComplete: () => {
              if (text.active) text.destroy();
              if (this.scene.areaBannerText === text) this.scene.areaBannerText = null;
            },
          });
        });
      },
    });
  }
}
